/* ============================================
   WISEL · CINEMATIC ENGINE
   Lenis smooth scroll + GSAP + ScrollTrigger
   ============================================ */

(function () {
  'use strict';

  const isTouch = window.matchMedia('(hover: none)').matches || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  let lenis = null;

  // Load CDN scripts
  function loadScript(src) {
    return new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  async function loadAll() {
    try {
      // Lenis (skip mobile)
      if (!isTouch) {
        await loadScript('https://unpkg.com/lenis@1.1.13/dist/lenis.min.js');
      }
      await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/gsap.min.js');
      await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/ScrollTrigger.min.js');
      // Configure ScrollTrigger early to avoid jank on iOS resize (address bar)
      if (window.ScrollTrigger) {
        window.ScrollTrigger.config({ ignoreMobileResize: true });
      }
      init();
    } catch (e) {
      console.warn('Wisel cinematic load fail', e);
    }
  }

  // Defer until idle on slow connections
  function startWhenIdle(){
    const slowConn = navigator.connection && navigator.connection.effectiveType && /2g/i.test(navigator.connection.effectiveType);
    if (slowConn) {
      // Esperar a interacción para no bloquear LCP
      const start = () => { loadAll(); document.removeEventListener('scroll', start); document.removeEventListener('click', start); };
      document.addEventListener('scroll', start, { once: true, passive: true });
      document.addEventListener('click', start, { once: true });
    } else if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadAll, { timeout: 2000 });
    } else {
      setTimeout(loadAll, 200);
    }
  }

  function init() {
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      // Notify any consumer waiting for GSAP+ScrollTrigger ready
      try { document.dispatchEvent(new CustomEvent('wisel:ready')); } catch(_){}
    }

    // ===== Lenis smooth scroll (desktop only) =====
    if (!isTouch && window.Lenis) {
      lenis = new window.Lenis({
        lerp: 0.12,
        smoothWheel: true,
        wheelMultiplier: 0.9,
        touchMultiplier: 0,
        syncTouch: false,
        autoRaf: false,
      });
      window.lenis = lenis;
      function raf(time) {
        lenis.raf(time);
        if (window.ScrollTrigger) window.ScrollTrigger.update();
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }

    // ===== Smooth anchor links · Lenis o fallback browser =====
    function smoothScrollTo(target, headerOffset = 80){
      if (window.lenis && typeof window.lenis.scrollTo === 'function') {
        window.lenis.scrollTo(target, { duration: 1.2, offset: -headerOffset });
      } else {
        const y = target.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]:not([href="#"])');
      if (!link) return;
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id) || document.querySelector(`[id="${id}"]`);
      if (!target) return;
      e.preventDefault();
      smoothScrollTo(target);
      history.pushState(null, '', '#' + id);
    });
    // Initial hash on page load · smooth scroll to anchor
    if (location.hash && location.hash.length > 1){
      const target = document.getElementById(location.hash.slice(1));
      if (target){
        // Esperar tras layout settle
        setTimeout(() => smoothScrollTo(target), 600);
      }
    }

    // ===== Reveal on scroll =====
    const reveals = document.querySelectorAll('[data-wisel-reveal]');
    if (reveals.length) reveals.forEach((el) => {
      window.gsap.fromTo(el,
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' }
        }
      );
    });

    // ===== Stagger children =====
    document.querySelectorAll('[data-wisel-stagger]').forEach((container) => {
      const items = container.querySelectorAll('[data-wisel-stagger-item]');
      if (!items.length) return;
      window.gsap.fromTo(items,
        { opacity: 0, y: 30, scale: 0.97 },
        {
          opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: { trigger: container, start: 'top 80%', toggleActions: 'play none none reverse' }
        }
      );
    });

    // ===== Pin sections (sticky reveal) =====
    document.querySelectorAll('[data-wisel-pin]').forEach((section) => {
      const pinFades = section.querySelectorAll('[data-pin-fade]');
      if (!pinFades.length) return;
      window.gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=100%',
          pin: true,
          pinSpacing: true,
          scrub: 0.8,
        }
      })
      .from(pinFades, { opacity: 0, y: 60, stagger: 0.15 });
    });

    // ===== Parallax images =====
    document.querySelectorAll('[data-wisel-parallax]').forEach((el) => {
      window.gsap.to(el, {
        yPercent: -15,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        }
      });
    });

    // ===== Tilt cards =====
    if (!isTouch) {
      document.querySelectorAll('[data-wisel-tilt]').forEach((card) => {
        card.style.transformStyle = 'preserve-3d';
        card.style.willChange = 'transform';
        card.addEventListener('mousemove', (e) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width - 0.5;
          const y = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform = `perspective(900px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) translateZ(8px)`;
        });
        card.addEventListener('mouseleave', () => {
          card.style.transform = 'perspective(900px) rotateY(0) rotateX(0) translateZ(0)';
        });
      });
    }

    // ===== HERO video parallax · zoom-out + slow scroll =====
    document.querySelectorAll('.wisel-hero-cine__video').forEach((vid) => {
      window.gsap.to(vid, {
        scale: 1.15,
        yPercent: 12,
        ease: 'none',
        scrollTrigger: {
          trigger: vid.closest('.wisel-hero-cine') || vid,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        }
      });
    });

    // ===== HERO title fade-out con scroll =====
    document.querySelectorAll('.wisel-hero-cine__content').forEach((el) => {
      window.gsap.to(el, {
        opacity: 0,
        y: -80,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top top', end: 'bottom 40%', scrub: 1 }
      });
    });

    // ===== HERO floater + badge fade-out + parallax on scroll =====
    document.querySelectorAll('.wisel-hero-cine__floater').forEach((el) => {
      window.gsap.to(el, {
        opacity: 0,
        y: 40,
        ease: 'none',
        scrollTrigger: { trigger: el.closest('.wisel-hero-cine') || el, start: 'top top', end: 'bottom 30%', scrub: 1 }
      });
    });
    document.querySelectorAll('.wisel-hero-cine__badge, .wisel-hero-cine__corner-top, .wisel-hero-cine__scroll-hint').forEach((el) => {
      window.gsap.to(el, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: { trigger: el.closest('.wisel-hero-cine') || el, start: 'top top', end: 'bottom 50%', scrub: 1 }
      });
    });

    // ===== Counters (números animados al entrar viewport) =====
    document.querySelectorAll('[data-wisel-counter]').forEach((el) => {
      const target = parseFloat(el.dataset.wiselCounter || el.textContent.replace(/[^0-9.]/g, '')) || 0;
      const decimals = (el.dataset.wiselCounter || '').includes('.') ? 1 : 0;
      const prefix = el.dataset.wiselPrefix || '';
      const suffix = el.dataset.wiselSuffix || '';
      const obj = { val: 0 };
      window.gsap.to(obj, {
        val: target,
        duration: 2.2,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' },
        onUpdate: () => {
          el.textContent = prefix + obj.val.toFixed(decimals) + suffix;
        }
      });
    });

    // ===== Scale-in on scroll (cards/images crecen al entrar) =====
    document.querySelectorAll('[data-wisel-scale]').forEach((el) => {
      window.gsap.fromTo(el,
        { scale: 0.88, opacity: 0 },
        {
          scale: 1, opacity: 1, duration: 1.2, ease: 'expo.out',
          scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' }
        }
      );
    });

    // ===== Text split reveal (palabras una a una) =====
    // Defensive: skip si el elemento tiene HTML hijo (perdería <em> tags)
    document.querySelectorAll('[data-wisel-text-reveal]').forEach((el) => {
      if (el.children.length > 0) return; // contiene <em> u otros · no romper
      const text = el.textContent.trim();
      if (!text) return;
      const words = text.split(/\s+/);
      el.innerHTML = words.map(w => `<span class="word" style="display:inline-block;overflow:hidden"><span class="word-inner" style="display:inline-block;will-change:transform">${w}</span></span>`).join(' ');
      const inners = el.querySelectorAll('.word-inner');
      window.gsap.fromTo(inners,
        { y: '100%', opacity: 0 },
        {
          y: '0%', opacity: 1, duration: 0.85, ease: 'expo.out', stagger: 0.06,
          scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none reverse' }
        }
      );
    });

    // ===== Image cinematic zoom in (kenburns on entry) =====
    document.querySelectorAll('[data-wisel-zoom]').forEach((el) => {
      const img = el.querySelector('img') || el;
      window.gsap.fromTo(img,
        { scale: 1.1 },
        {
          scale: 1, duration: 1.8, ease: 'expo.out',
          scrollTrigger: { trigger: el, start: 'top 80%', toggleActions: 'play none none reverse' }
        }
      );
    });

    // ===== Section gradient sweep (color overlay que pasa al hacer scroll) =====
    document.querySelectorAll('[data-wisel-sweep]').forEach((el) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:absolute;inset:0;background:linear-gradient(135deg,transparent 0%,rgba(217,168,44,.12) 50%,transparent 100%);pointer-events:none;mix-blend-mode:overlay;opacity:0';
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.appendChild(overlay);
      window.gsap.to(overlay, {
        opacity: 1,
        backgroundPositionX: '200%',
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    // ===== Footer · pin reveal estilo Apple =====
    document.querySelectorAll('[data-wisel-foot-reveal]').forEach((el) => {
      window.gsap.fromTo(el,
        { yPercent: 30, opacity: 0.7 },
        {
          yPercent: 0, opacity: 1, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 60%', scrub: 0.5 }
        }
      );
    });

    // ===== Smart video playback (mobile-friendly · lazy load) =====
    const videos = Array.from(document.querySelectorAll('video[autoplay]'));
    videos.forEach(v => { v.muted = true; v.playsInline = true; });
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          const v = e.target;
          if (e.isIntersecting) {
            // Lazy load · cambia preload a auto la primera vez
            if (v.preload === 'none') {
              v.preload = 'auto';
              v.load();
            }
            v.play().catch(() => {});
          } else {
            try { v.pause(); } catch (_) {}
          }
        });
      }, { threshold: 0.15, rootMargin: isTouch ? '200px 0px' : '500px 0px' });
      videos.forEach(v => obs.observe(v));
    }

    // ===== Force first gesture play (autoplay fallback) =====
    const triggerPlay = () => {
      videos.forEach(v => v.play().catch(() => {}));
      document.removeEventListener('click', triggerPlay);
      document.removeEventListener('touchstart', triggerPlay);
      document.removeEventListener('scroll', triggerPlay);
    };
    document.addEventListener('click', triggerPlay, { once: true });
    document.addEventListener('touchstart', triggerPlay, { once: true });
    document.addEventListener('scroll', triggerPlay, { once: true, passive: true });

    // ===== Magnetic CTAs (desktop only) =====
    if (!isTouch && window.gsap) {
      document.querySelectorAll('[data-wisel-magnetic], .wisel-hero-cine__cta, .wpd__cta, .wcmp__cta, .wisel-cta__btn').forEach(el => {
        el.addEventListener('mousemove', e => {
          const r = el.getBoundingClientRect();
          window.gsap.to(el, {
            x: (e.clientX - r.left - r.width / 2) * 0.3,
            y: (e.clientY - r.top - r.height / 2) * 0.3,
            duration: 0.4,
            ease: 'power3.out'
          });
        });
        el.addEventListener('mouseleave', () => {
          window.gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenIdle);
  } else {
    startWhenIdle();
  }
})();
